/**
 * Staff list + create-new form + leaderboard (30d).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Download } from "lucide-react";

import api, { extractErr } from "../api";
import {
    Badge,
    Button,
    Card,
    EmptyState,
    Field,
    Input,
    PageHeader,
    Select,
} from "../ui";

const PRESETS = [
    { v: "today", l: "Oggi" },
    { v: "yesterday", l: "Ieri" },
    { v: "7d", l: "Ultimi 7 gg" },
    { v: "30d", l: "Ultimi 30 gg" },
    { v: "month", l: "Mese corrente" },
    { v: "prev_month", l: "Mese scorso" },
    { v: "all", l: "Sempre" },
];

export default function StaffList() {
    const [rows, setRows] = useState([]);
    const [preset, setPreset] = useState("30d");
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ first_name: "", last_name: "", role: "" });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    const reload = () => {
        setLoading(true);
        api.get(`/stats/staff?preset=${preset}`)
            .then((r) => setRows(r.data.rows))
            .catch((e) => setErr(extractErr(e)))
            .finally(() => setLoading(false));
    };
    useEffect(reload, [preset]); // eslint-disable-line react-hooks/exhaustive-deps

    const create = async (e) => {
        e.preventDefault();
        setErr("");
        try {
            await api.post("/staff", form);
            setForm({ first_name: "", last_name: "", role: "" });
            setCreating(false);
            reload();
        } catch (e) {
            setErr(extractErr(e));
        }
    };

    const deactivate = async (id) => {
        if (!window.confirm("Disattivare il collaboratore? I dati storici restano intatti."))
            return;
        try {
            await api.delete(`/staff/${id}`);
            reload();
        } catch (e) {
            setErr(extractErr(e));
        }
    };

    const exportCsv = async () => {
        setErr("");
        try {
            const res = await api.get(`/stats/staff.csv?preset=${preset}`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            const cd = res.headers["content-disposition"] || "";
            const m = cd.match(/filename="?([^";]+)"?/);
            a.download = m
                ? m[1]
                : `collaboratori-${preset}-${new Date()
                      .toISOString()
                      .slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(extractErr(e, "Export CSV fallito"));
        }
    };

    return (
        <div data-testid="staff-list-page">
            <PageHeader
                title="Collaboratori"
                description="Gestisci i collaboratori e assegna loro i tag NFC. Lo storico delle assegnazioni è immutabile."
                actions={
                    <>
                        <Button
                            variant="secondary"
                            onClick={exportCsv}
                            data-testid="staff-export-csv"
                        >
                            <Download size={14} /> Esporta CSV
                        </Button>
                        <Button
                            onClick={() => setCreating((c) => !c)}
                            data-testid="staff-new-toggle"
                        >
                            <Plus size={14} /> Nuovo collaboratore
                        </Button>
                    </>
                }
            />

            {creating && (
                <Card className="p-5 mb-6">
                    <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <Field label="Nome">
                            <Input
                                required
                                value={form.first_name}
                                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                                data-testid="staff-form-first-name"
                            />
                        </Field>
                        <Field label="Cognome">
                            <Input
                                required
                                value={form.last_name}
                                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                                data-testid="staff-form-last-name"
                            />
                        </Field>
                        <Field label="Ruolo (opzionale)">
                            <Input
                                value={form.role}
                                onChange={(e) => setForm({ ...form, role: e.target.value })}
                                data-testid="staff-form-role"
                            />
                        </Field>
                        <div className="flex items-end">
                            <Button type="submit" data-testid="staff-form-submit">
                                Crea
                            </Button>
                        </div>
                    </form>
                </Card>
            )}

            <div className="flex items-center gap-2 mb-4">
                <span className="text-xs text-slate-500 uppercase tracking-wider">Periodo</span>
                <Select
                    value={preset}
                    onChange={(e) => setPreset(e.target.value)}
                    className="max-w-[180px]"
                    data-testid="staff-preset"
                >
                    {PRESETS.map((p) => (
                        <option key={p.v} value={p.v}>
                            {p.l}
                        </option>
                    ))}
                </Select>
            </div>

            {err && <div className="text-red-600 text-sm mb-3">{err}</div>}

            <Card className="overflow-hidden">
                {loading ? (
                    <div className="p-8 text-slate-500 text-sm">Caricamento…</div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        title="Nessun collaboratore"
                        description="Aggiungi il tuo primo collaboratore per assegnargli un tag NFC."
                        action={<Button onClick={() => setCreating(true)}>Crea collaboratore</Button>}
                    />
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-5 py-3">Nome</th>
                                <th className="text-left px-5 py-3">Ruolo</th>
                                <th className="text-left px-5 py-3">Tag attivi</th>
                                <th className="text-right px-5 py-3">Oggi</th>
                                <th className="text-right px-5 py-3">7 gg</th>
                                <th className="text-right px-5 py-3">Periodo</th>
                                <th className="text-right px-5 py-3">Totali</th>
                                <th className="text-right px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-5 py-3">
                                        <Link
                                            to={`/admin/collaboratori/${r.id}`}
                                            className="font-medium hover:underline"
                                            data-testid={`staff-row-${r.id}`}
                                        >
                                            {r.first_name} {r.last_name}
                                        </Link>
                                        {!r.active && (
                                            <Badge tone="danger">disattivato</Badge>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-slate-500">—</td>
                                    <td className="px-5 py-3 text-slate-600">
                                        {r.current_tags.length === 0 ? (
                                            <span className="text-slate-400">nessuno</span>
                                        ) : (
                                            r.current_tags.join(", ")
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-right tabular-nums">{r.today}</td>
                                    <td className="px-5 py-3 text-right tabular-nums">{r.last_7d}</td>
                                    <td className="px-5 py-3 text-right tabular-nums font-semibold">
                                        {r.window_total}
                                    </td>
                                    <td className="px-5 py-3 text-right tabular-nums text-slate-500">
                                        {r.total}
                                    </td>
                                    <td className="px-5 py-3 text-right">
                                        {r.active && (
                                            <button
                                                onClick={() => deactivate(r.id)}
                                                className="text-slate-400 hover:text-red-600 p-1 rounded"
                                                title="Disattiva"
                                                data-testid={`staff-deactivate-${r.id}`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>
        </div>
    );
}
